import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { authService } from '../services/api';

export default function Navbar() {
  const navigate = useNavigate();
  const isAuthenticated = authService.isAuthenticated();
  const currentUser = isAuthenticated ? authService.getCurrentUser() : null;

  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => setMenuOpen(prev => !prev);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
    setMenuOpen(false);
  };

  // 🔥 Bloquear scroll cuando menú está abierto
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [menuOpen]);

  return (
    <header className="w-full shadow-sm bg-white relative z-50">

      <div className="bg-[color:var(--dark)] text-white text-center py-1">
        Cronograma de actividades de la semana
      </div>

      <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-4">
          <Logo />
          <div>
            <div className="text-2xl font-bold">SPT</div>
            <div className="text-sm text-gray-600">La Guardia, Santa Cruz</div>
          </div>
        </Link>

        {/* Desktop Menu */}
        <nav className="hidden md:flex items-center gap-6 text-base font-semibold text-gray-700">

          <Link to="/">Inicio</Link>
          <Link to="/proposals">Propuestas</Link>
          <Link to="/suggestions">Sugerencias</Link>
          <Link to="/team">Equipo</Link>

          {currentUser?.role === 'admin' && (
            <Link to="/dashboard">Dashboard</Link>
          )}

          {/* Redes Desktop */}
          <div className="flex items-center gap-4 ml-4 border-l pl-4">

            <a
              href="https://www.facebook.com/noemibejaranoaoficial"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:scale-110 transition duration-200"
            >
              <svg viewBox="0 0 24 24" fill="#1877F2" className="w-6 h-6">
                <path d="M22 12.07C22 6.48 17.52 2 11.93 2S2 6.48 2 12.07c0 5.01 3.66 9.17 8.44 9.93v-7.02H7.9v-2.9h2.54V9.41c0-2.5 1.49-3.88 3.77-3.88 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.77l-.44 2.9h-2.33V22c4.78-.76 8.44-4.92 8.44-9.93z" />
              </svg>
            </a>

            <a
              href="https://www.tiktok.com/@noemibejaranoaparicio"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:scale-110 transition duration-200"
            >
              <svg viewBox="0 0 256 256" className="w-6 h-6 fill-black">
                <path d="M232 80.4c-23.1 0-44.3-7.5-61.4-20.2V168c0 40.7-33 73.7-73.7 73.7S23.3 208.7 23.3 168 56.3 94.3 97 94.3c4.6 0 9.1.4 13.4 1.2v37.5c-4.2-1.3-8.7-2-13.4-2-20.6 0-37.3 16.7-37.3 37.3s16.7 37.3 37.3 37.3 37.3-16.7 37.3-37.3V14h36.6c2.8 23.5 22.9 41.8 47.1 41.8 4.2 0 8.3-.5 12.2-1.5v26.1z"/>
              </svg>
            </a>

          </div>

          {/* Auth Desktop */}
          <div className="flex items-center gap-2 ml-4">
            {isAuthenticated ? (
              <>
                <span className="text-sm text-gray-600">{currentUser?.email}</span>
                <button
                  onClick={handleLogout}
                  className="text-sm px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                >
                  Salir
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="px-3 py-2 rounded bg-[color:var(--primary)] text-white font-semibold hover:opacity-90"
              >
                Iniciar sesión
              </Link>
            )}
          </div>

        </nav>

        {/* Mobile Button */}
        <div className="md:hidden">
          <button
            onClick={toggleMenu}
            className="p-2 rounded border"
            aria-label="Abrir menú"
          >
            ☰
          </button>
        </div>

      </div>

      {/* Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Mobile Panel */}
      <div className={`fixed top-0 right-0 h-full w-3/5 bg-white shadow-lg z-50 transform transition-transform duration-300 ${
        menuOpen ? "translate-x-0" : "translate-x-full"
      }`}>

        <div className="p-6 relative flex flex-col gap-4 text-base font-semibold text-gray-700">

          {/* Close Button */}
          <button
            onClick={() => setMenuOpen(false)}
            className="absolute top-4 right-4 text-2xl font-bold"
          >
            ✕
          </button>

          <div className="mt-10 flex flex-col gap-4">
            <Link to="/" onClick={() => setMenuOpen(false)}>Inicio</Link>
            <Link to="/proposals" onClick={() => setMenuOpen(false)}>Propuestas</Link>
            <Link to="/suggestions" onClick={() => setMenuOpen(false)}>Sugerencias</Link>
            <Link to="/team" onClick={() => setMenuOpen(false)}>Equipo</Link>

            {currentUser?.role === 'admin' && (
              <Link to="/dashboard" onClick={() => setMenuOpen(false)}>
                Dashboard
              </Link>
            )}
          </div>

          {/* Redes Mobile */}
          <div className="flex items-center gap-4 mt-6 border-t pt-4">
            <a href="https://www.facebook.com/noemibejaranoaoficial" target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" fill="#1877F2" className="w-6 h-6">
                <path d="M22 12.07C22 6.48 17.52 2 11.93 2S2 6.48 2 12.07c0 5.01 3.66 9.17 8.44 9.93v-7.02H7.9v-2.9h2.54V9.41c0-2.5 1.49-3.88 3.77-3.88 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.77l-.44 2.9h-2.33V22c4.78-.76 8.44-4.92 8.44-9.93z" />
              </svg>
            </a>

            <a href="https://www.tiktok.com/@noemibejaranoaparicio" target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 256 256" className="w-6 h-6 fill-black">
                <path d="M232 80.4c-23.1 0-44.3-7.5-61.4-20.2V168c0 40.7-33 73.7-73.7 73.7S23.3 208.7 23.3 168 56.3 94.3 97 94.3c4.6 0 9.1.4 13.4 1.2v37.5c-4.2-1.3-8.7-2-13.4-2-20.6 0-37.3 16.7-37.3 37.3s16.7 37.3 37.3 37.3 37.3-16.7 37.3-37.3V14h36.6c2.8 23.5 22.9 41.8 47.1 41.8 4.2 0 8.3-.5 12.2-1.5v26.1z"/>
              </svg>
            </a>
          </div>

          {/* Auth Mobile */}
          <div className="flex flex-col gap-2 mt-4">
            {isAuthenticated ? (
              <>
                <span className="text-sm text-gray-600">{currentUser?.email}</span>
                <button
                  onClick={handleLogout}
                  className="text-sm px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                >
                  Salir
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
                className="px-3 py-2 rounded bg-[color:var(--primary)] text-white font-semibold text-center"
              >
                Iniciar sesión
              </Link>
            )}
          </div>

        </div>
      </div>

    </header>
  );
}